import { useEffect, useRef, useState } from 'react'
import { ExternalLink, Radio, Square, Volume2 } from 'lucide-react'
import type { AtcFeed } from '@/types/airTraffic'
import { AIR, FONT_MONO } from './theme'

type Status = 'connecting' | 'live' | 'error'

/** Plays a LiveATC stream straight from the analyst's browser, the same way
 * LiveATC's own player does. No crossOrigin attribute: the stream sends no
 * CORS headers, and a plain <audio> element doesn't need any. Lives in the
 * top bar so audio keeps playing while the analyst inspects other things. */
export default function AtcPlayer({ feed, onStop }: { feed: AtcFeed | null; onStop: () => void }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [status, setStatus] = useState<Status>('connecting')
  const [volume, setVolume] = useState(0.8)

  useEffect(() => {
    setStatus('connecting')
  }, [feed?.mount])

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume
  }, [volume])

  if (!feed) return null
  const color = status === 'live' ? AIR.accent : status === 'error' ? AIR.emergency : AIR.muted

  return (
    <div
      className="flex h-7 items-center gap-2 rounded-sm px-2 text-[11px]"
      style={{ background: AIR.panel2, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}` }}
    >
      <audio
        key={feed.mount}
        ref={audioRef}
        src={feed.stream_url}
        autoPlay
        onPlaying={() => setStatus('live')}
        onWaiting={() => setStatus('connecting')}
        onError={() => setStatus('error')}
      />
      <Radio className={`h-3.5 w-3.5 ${status === 'live' ? 'animate-pulse' : ''}`} style={{ color }} />
      <span className="max-w-[160px] truncate" style={{ color: AIR.text, fontFamily: FONT_MONO }} title={feed.name}>
        {feed.name}
      </span>
      <span className="uppercase tracking-wider" style={{ color, fontSize: 9 }}>
        {status === 'live' ? 'Live' : status === 'error' ? 'Offline' : 'Tuning'}
      </span>
      {status === 'error' ? (
        <a
          href={`https://www.liveatc.net/search/?icao=${feed.icao.toLowerCase()}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
          style={{ color: AIR.text }}
          title="This stream isn't broadcasting. Find the airport's current feeds on LiveATC."
        >
          LiveATC <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <label className="flex items-center gap-1" title="Volume">
          <Volume2 className="h-3 w-3" style={{ color: AIR.muted }} />
          <input
            id="atc-volume"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="h-1 w-14 accent-teal-500"
          />
        </label>
      )}
      <button type="button" onClick={onStop} title="Stop" className="rounded-sm p-0.5 hover:bg-white/5">
        <Square className="h-3 w-3" style={{ color: AIR.muted }} />
      </button>
    </div>
  )
}
